/**
 * TrackingPathPanel — one subject's trail drawn WHERE IT HAPPENED, docked under the Track tab.
 *
 * ─── WHY THIS REPLACED THE TIMELINE STRIP ───
 *
 * The strip answers "where in the day was the activity", and on the Search tab that is the
 * question the answer above it cannot answer for itself. On the Track tab it is the wrong
 * question twice over: a trail already reads in time order, stop by stop, with the dwell and
 * the gap written between them — so a chart of events per hour restates the one thing the
 * answer says best, and says nothing about the thing it cannot say at all.
 *
 * WHICH IS WHERE. "Gate 1 → Reception → Server Room" is a list of names an operator has to
 * hold a building in their head to read. The same three stops on a plan are a route: through
 * the lobby, past the desk, and into a room they should not have reached. That is what this
 * panel is for, and it is the only part of following somebody that a list genuinely cannot do.
 *
 * ─── IT DRAWS WHAT THE TIMELINE LISTS ───
 *
 * The same stops, in the same order, from the same fold (`buildStops`, exported by
 * ResultTimeline for exactly this). Line for line, what the operator is reading above is what
 * is drawn below, so the plan can never show a stop the list does not — which a second fold
 * with its own idea of where a visit ends would do within a release.
 *
 * It used to draw ONE SELECTED SUBJECT out of a page of trails. That is gone with the trails:
 * the tab's job is "where did this go", and making the operator pick an individual before it
 * would show them a route was a click in front of the answer.
 *
 * WHICH MEANS IT CAN BE ASKED A QUESTION IT MUST REFUSE. A page of stops belonging to six
 * different ENROLLED people is not a route, and a line through it would join one person's
 * doorway to another's. The panel counts the identities in the stops it is given and says so
 * instead of drawing — see `identities` below.
 *
 * An attribute search is NOT that case, and treating it as one was a bug: "red top, black
 * trousers" produces no identity at all, only a per-camera tracker id that differs at every
 * camera, so the old count read one person's walk through a building as six strangers and
 * refused the one drawing the operator had come for. Those trails are drawn now, captioned as
 * a description rather than a person — see `byDescription`.
 *
 * ─── TWO CANVASES, ONE PATH ───
 *
 * GIS (the floor plan) and Map (the geographic basemap) answer different questions — "which
 * door" against "which building" — and which one a trail wants depends on the trail. So the
 * panel offers both and OPENS ON WHICHEVER ACTUALLY HOLDS MORE OF THIS TRAIL'S CAMERAS: an
 * indoor route opens on the plan, a cross-site one on the map, without anybody choosing.
 *
 * A canvas with none of the trail's cameras on it is offered but disabled, with the reason in
 * its tooltip — hiding it would leave an operator wondering whether the product has the view
 * at all, which is a different question from whether these cameras are on it.
 *
 * ─── STOPS THAT CANNOT BE DRAWN ARE COUNTED, NEVER DROPPED SILENTLY ───
 *
 * A camera with no pin and no coordinates cannot be on either canvas. The path then joins the
 * stops it can place, which is a route with a piece missing — and a route with a piece missing
 * read as a whole route is the failure this panel would otherwise introduce. So the header
 * always states how many of the trail's stops are drawn out of how many there are, and the
 * shortfall is named.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  GlobeAltIcon,
  MapIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import GeoTileMap, { type GeoTileMapHandle } from '../map/GeoTileMap';
import BasemapSwitcher from '../map/BasemapSwitcher';
import { useBasemap } from '../map/basemaps';
import MapMarker from '../map/MapMarker';
import DockShell from './DockShell';
import { useApi } from '../../api/ApiProvider';
import type {
  HexCamera,
  GeoMapConfig,
  GeoPlacement,
  MapLayoutDetail,
  MapLayoutSummary,
  MapPlacedCamera,
} from '../../api/contracts';
import type { Stop } from './ResultTimeline';

export type PathView = 'gis' | 'map';

/**
 * The dock's chrome — fold, drag-to-resize, full screen — is `DockShell`, shared with the Search
 * tab's timeline. It used to live here, which is why the timeline had no drag and this had no
 * full screen: chrome written twice is chrome that diverges.
 *
 * `ceilingFrom` moved with it and is re-exported so `scripts/test-tracking-path.ts` keeps
 * asserting the height arithmetic from the file it has always imported.
 */
export { ceilingFrom } from './DockShell';

/** One drawable stop: where it is, and which positions on the trail it accounts for. */
export interface PathNode {
  cameraId: string;
  x: number;
  y: number;
  /**
   * The stop numbers this point stands for, 1-based over the WHOLE trail.
   *
   * A trail that returns to a camera has two stops at one position, and two pins on one spot
   * is a number nobody can read. They share a pin and the pin carries both — which is also the
   * truer statement: the subject was at that place twice.
   *
   * Numbered over the whole trail, not over the drawable ones, so an undrawable stop leaves a
   * gap in the numbering rather than silently renumbering the rest and making the path look
   * complete.
   */
  ordinals: number[];
}

/** One walk along a connection, in trail order. */
export interface PathMove {
  /** 1-based position in the trail — the move INTO the stop it reached, so it matches that pin. */
  seq: number;
  /** Ran `to` → `from`, against the way the connection was first walked. */
  reverse: boolean;
}

/**
 * One connection between two cameras: EVERY move between that pair, drawn as a single line.
 *
 * ── Why one line per pair, with the numbers on it ──
 *
 * A polyline through the stops loses repeats: Gate 1 → Lobby → Gate 1 traces the return over the
 * outbound pixel for pixel. The fix used to be one arc per move, each repeat fanning further out
 * — which on a busy corridor became a sheaf of loops that hid the plan under it. Operators asked
 * for one line per connection instead. Nothing is lost: the badge on it lists every stop number
 * that crossed it ("2 · 5 · 7"), and an arrowhead is drawn for each direction it was walked, so
 * an out-and-back still reads as two-way.
 */
export interface PathEdge {
  /** Ends in the direction of the FIRST move between the pair. */
  from: PathNode;
  to: PathNode;
  /** Every move along this connection, in trail order. Never empty. */
  moves: PathMove[];
  /** How far, and which way, the line bends off the straight chord. See `layEdges`. */
  bow: number;
}

/**
 * A placed camera drawn UNDER the route, so the canvas shows the site and not only the trail.
 * Same coordinate convention as `PathNode`: plan fractions, or longitude/latitude.
 */
interface BackdropCamera {
  cameraId: string;
  x: number;
  y: number;
  /** Some result stopped here — drawn in the route's ink rather than grey. */
  visited: boolean;
  /** The map placement's own name, for when the page's camera list does not hold it. */
  name?: string;
  /** Facing and spread, drawn as the Alert Board's cone. */
  rotation?: number | null;
  fov?: number | null;
}

/** Live/recording state per camera, from the same feed the Alert Board's pins use. */
type CameraStatus = Record<string, HexCamera>;

/** The Alert Board's fallback spread for a camera whose field of view was never set. */
const DEFAULT_FOV = 90;

/** The path in draw order, plus what it could not place. */
interface Path {
  nodes: PathNode[];
  /** Points in trail order — a return to one camera appears twice, as it should. */
  line: Array<{ x: number; y: number }>;
  /** Every move between two DIFFERENT placed cameras, in order. */
  edges: PathEdge[];
  placed: number;
  total: number;
}

/**
 * How far a connection bends off the straight chord, as a FRACTION of its own length.
 *
 * Just enough that a line passing through a third camera's pin on its way does not read as
 * stopping there, and not so much that it looks like a detour — 0.13 per move (fanning further
 * for every repeat) was the old value, and operators found the result far too curvy.
 */
export const EDGE_BOW = 0.05;

/**
 * Fold the trail's moves into one connection per camera pair.
 *
 * The sign of the bend comes from comparing the two camera ids, not from the order of travel,
 * so a pair bends to the same side whichever way it was first walked.
 */
export function layEdges(nodes: PathNode[], order: string[]): PathEdge[] {
  const byId = new Map(nodes.map((n) => [n.cameraId, n]));
  const byPair = new Map<string, PathEdge>();
  const edges: PathEdge[] = [];

  for (let i = 1; i < order.length; i += 1) {
    const from = byId.get(order[i - 1]);
    const to = byId.get(order[i]);
    // A stop split from its neighbour by the gap rule, at the same camera, is not a MOVE. The
    // pin's own ordinals ("1,2") already say the subject was there twice.
    if (!from || !to || from === to) continue;
    const pair = from.cameraId < to.cameraId
      ? `${from.cameraId}|${to.cameraId}`
      : `${to.cameraId}|${from.cameraId}`;
    const edge = byPair.get(pair);
    if (edge) {
      edge.moves.push({ seq: i + 1, reverse: edge.from !== from });
      continue;
    }
    const side = from.cameraId < to.cameraId ? 1 : -1;
    const created: PathEdge = { from, to, moves: [{ seq: i + 1, reverse: false }], bow: side * EDGE_BOW };
    byPair.set(pair, created);
    edges.push(created);
  }
  return edges;
}

/** How many stop numbers a connection's badge spells out before it says "+N" instead. */
const BADGE_MAX = 4;

/** The badge text: the stop numbers that crossed this connection, "2 · 5 · 7" or "2 · 5 · 7 · 9 +3". */
export function edgeBadge(e: PathEdge) {
  const seqs = e.moves.map((m) => m.seq);
  const shown = seqs.slice(0, BADGE_MAX).join(' · ');
  return seqs.length > BADGE_MAX ? `${shown} +${seqs.length - BADGE_MAX}` : shown;
}

/** Hover text: every move along the connection, by the stop numbers it joined. */
export function edgeTitle(e: PathEdge) {
  return e.moves.map((m) => `${m.seq - 1} → ${m.seq}`).join(', ');
}

/**
 * A connection as an SVG path, where its badge sits, and one arrowhead per direction walked.
 *
 * The badge takes the middle of the line, so the arrowheads sit either side of it, each towards
 * the end it points at: the forward one near `to`, the return one near `from`.
 */
export function edgeGeometry(e: PathEdge) {
  const dx = e.to.x - e.from.x;
  const dy = e.to.y - e.from.y;
  const len = Math.hypot(dx, dy) || 1;
  // The control point, out along the perpendicular. A quadratic through it passes half as far
  // off the chord as the control point sits, which is why the bow is doubled here.
  const cx = (e.from.x + e.to.x) / 2 - (dy / len) * len * e.bow * 2;
  const cy = (e.from.y + e.to.y) / 2 + (dx / len) * len * e.bow * 2;
  /** The point at `t` along the curve, and the angle of its tangent there (degrees, SVG y-down). */
  const at = (t: number) => {
    const u = 1 - t;
    const tx = 2 * u * (cx - e.from.x) + 2 * t * (e.to.x - cx);
    const ty = 2 * u * (cy - e.from.y) + 2 * t * (e.to.y - cy);
    return {
      x: u * u * e.from.x + 2 * u * t * cx + t * t * e.to.x,
      y: u * u * e.from.y + 2 * u * t * cy + t * t * e.to.y,
      angle: (Math.atan2(ty, tx) * 180) / Math.PI,
    };
  };
  const forward = e.moves.some((m) => !m.reverse);
  const backward = e.moves.some((m) => m.reverse);
  const arrows: Array<{ x: number; y: number; angle: number }> = [];
  if (forward) arrows.push(at(0.72));
  if (backward) {
    const p = at(0.28);
    arrows.push({ ...p, angle: p.angle + 180 });
  }
  const mid = at(0.5);
  return {
    d: `M ${e.from.x} ${e.from.y} Q ${cx} ${cy} ${e.to.x} ${e.to.y}`,
    mx: mid.x,
    my: mid.y,
    arrows,
  };
}

function buildPath(
  stops: Stop[],
  positionOf: (cameraId: string) => { x: number; y: number } | null,
): Path {
  const byCamera = new Map<string, PathNode>();
  const line: Array<{ x: number; y: number }> = [];
  let placed = 0;

  stops.forEach((stop, i) => {
    const at = positionOf(stop.cameraId);
    if (!at) return;
    placed += 1;
    line.push(at);
    const seen = byCamera.get(stop.cameraId);
    if (seen) seen.ordinals.push(i + 1);
    else byCamera.set(stop.cameraId, { cameraId: stop.cameraId, x: at.x, y: at.y, ordinals: [i + 1] });
  });

  const nodes = [...byCamera.values()];
  const order = stops.map((st) => st.cameraId).filter((id) => byCamera.has(id));
  return { nodes, line, edges: layEdges(nodes, order), placed, total: stops.length };
}

/**
 * WHERE THE PLAN IS DRAWN INSIDE THE CONTAINER.
 *
 * ─── WHY THE WHOLE PLAN, FITTED ───
 *
 * This used to crop: a box around the route, shaped like the dock, magnified to fill it. That
 * answers "show me the route" and gets two other things wrong. A crop tight enough to be useful
 * blows an uploaded floor plan up past the resolution it was scanned at, and — because the
 * placements are fractions of the stored `image_width × image_height`, which the drawing has to
 * honour for a pin to land on its door — any disagreement between those numbers and the file is
 * magnified with it. Both read as "the plan is not rendering properly", and both were.
 *
 * So the plan is drawn WHOLE, scaled to fit the container and centred in it, at its own
 * proportions. The operator sees the building; the dock is draggable and goes full screen when
 * they want it bigger. What is left over is canvas, not a stretched picture.
 *
 * Everything else on the canvas — the pins, the legs, the arrowheads, the labels — is positioned
 * against THIS rectangle rather than against the plan's stored pixel size, so the route is drawn
 * wherever the plan ended up and at whatever size it ended up.
 *
 * Exported for `scripts/test-tracking-path.ts`.
 */
export interface PlanRect { x: number; y: number; w: number; h: number; scale: number }

export function fitPlan(
  container: { w: number; h: number },
  image: { w: number; h: number },
): PlanRect | null {
  if (!(container.w > 0 && container.h > 0 && image.w > 0 && image.h > 0)) return null;
  // `min`, so the whole plan is inside the box on both axes — `max` would cover the container
  // and cut the plan, which is the crop this replaced.
  const scale = Math.min(container.w / image.w, container.h / image.h);
  const w = image.w * scale;
  const h = image.h * scale;
  return { x: (container.w - w) / 2, y: (container.h - h) / 2, w, h, scale };
}

/** A placement (fractions of the plan) at its pixel position on the drawn rectangle. */
export function placeOn(rect: PlanRect, at: { x: number; y: number }) {
  return { x: rect.x + at.x * rect.w, y: rect.y + at.y * rect.h };
}

export interface TrackingPathPanelProps {
  /**
   * The stops the timeline is listing, in TIME order (oldest first) — `buildStops`' own order,
   * before the list applies its reading direction. A route is what happened, so it is drawn
   * forwards whichever way the list above is being read.
   */
  stops: Stop[];
  cameraName: (id: string) => string | undefined;
  /** Changes when the operator switches node (cloud) — drops every cached placement. */
  nodeKey?: string;
  /** Fill the parent column (the Track page's centre pane) instead of docking under the answer. */
  fill?: boolean;
  /** With `fill`: the header button that folds the pane away. */
  onCollapse?: () => void;
}

export default function TrackingPathPanel({
  stops, cameraName, nodeKey = '', fill = false, onCollapse,
}: TrackingPathPanelProps) {
  const { mapLayouts, geoMap, hexAlerts } = useApi();

  /**
   * Live / recording state, so a camera pin here is the Alert Board's pin: the same online or
   * offline disc and the same two status dots. One snapshot per open — a search is not a
   * monitoring screen. A failure leaves every pin drawn as online-unknown, never missing.
   */
  const [cameraStatus, setCameraStatus] = useState<CameraStatus>({});
  useEffect(() => {
    let live = true;
    hexAlerts?.loadCameras()
      .then((cams) => { if (live) setCameraStatus(Object.fromEntries(cams.map((c) => [c.id, c]))); })
      .catch(() => {});
    return () => { live = false; };
  }, [hexAlerts, nodeKey]);

  /**
   * The canvas the operator is on. Null until they choose, so `active` below opens on the Map
   * (falling back to the plan only where this node has no Map View configured).
   */
  const [view, setView] = useState<PathView | null>(null);

  const [placedCameras, setPlacedCameras] = useState<Record<string, MapPlacedCamera> | null>(null);
  const [layouts, setLayouts] = useState<MapLayoutSummary[]>([]);
  const [detail, setDetail] = useState<MapLayoutDetail | null>(null);
  const [planUrl, setPlanUrl] = useState<string | null>(null);
  /**
   * The plan fetch has FINISHED, whether or not it produced a plan.
   *
   * Needed separately from `detail` because a failed fetch leaves `detail` null, which is
   * indistinguishable from a fetch still running — and the panel draws two different things for
   * those: a spinner, or a verdict that nothing here is placed. Without this, a layout that
   * 404s pinned the panel on "Loading…" for ever.
   */
  const [layoutSettled, setLayoutSettled] = useState(false);
  const [geo, setGeo] = useState<GeoPlacement[] | null>(null);
  const [geoConfig, setGeoConfig] = useState<GeoMapConfig | null>(null);
  // Same choice and storage as the Alert Board's Map View — one preference per browser.
  const basemap = useBasemap(geoConfig);
  const [error, setError] = useState<string | null>(null);

  const mapRef = useRef<GeoTileMapHandle>(null);

  const cameraIds = useMemo(
    () => [...new Set(stops.map((st) => st.cameraId))],
    [stops],
  );

  /**
   * How many IDENTITIES these stops belong to — see `Stop.identified`.
   *
   * A route is one thing moving. Two enrolled people's stops joined by a line is not a route, it
   * is a line between strangers, and it reads as one person's movement because that is what a
   * line between pins means. That refusal stands.
   *
   * WHAT IT USED TO COUNT was tracker keys, and a tracker id is unique only within one camera.
   * So an attribute search — "red top, black trousers", the thing the Track tab is most useful
   * for — produced a fresh key at every camera the person passed, reported "6 different
   * subjects", and refused to draw the one answer it was asked for. The path was reachable only
   * by name or by plate, which is precisely the case where the operator already knows who they
   * are following.
   */
  const identities = useMemo(
    () => new Set(stops.filter((st) => st.identified).map((st) => st.subjectId)).size,
    [stops],
  );
  /** Separate tracks, identified or not — what `byDescription` counts to be honest about. */
  const tracks = useMemo(
    () => new Set(stops.filter((st) => st.tracked).map((st) => st.subjectId)).size,
    [stops],
  );
  /** One identity, or none to speak of — drawable either way. Two or more is not a route. */
  const oneSubject = identities <= 1;
  /**
   * The trail is a DESCRIPTION's, not a person's: every stop matched the filters, and nothing in
   * the data says they are the same individual.
   *
   * Drawn, because it is the answer to the question that was asked, and captioned, because the
   * line would otherwise assert an identity the system cannot support.
   */
  const byDescription = oneSubject && identities === 0 && tracks > 1;
  /**
   * The stops a ROUTE may be drawn through — none when there is nothing to follow or when the
   * stops belong to several people. The canvas itself is drawn either way: in the Track page's
   * centre pane the plan or map is the page's backdrop, not a reward for a drawable trail.
   */
  /**
   * Every IDENTIFIED subject in these stops, busiest first — the "Following" picker's choices.
   *
   * Several enrolled people (or plates) in one result set still may not be joined into one
   * line. But refusing to draw anything left the centre pane empty on the most ordinary
   * search there is, so the busiest subject's route is drawn by default and the picker swaps
   * it for another. One route at a time, never a line between two of them.
   */
  const subjects = useMemo(() => {
    const tally = new Map<string, { id: string; label: string; stops: number }>();
    for (const st of stops) {
      if (!st.identified) continue;
      const row = tally.get(st.subjectId);
      if (row) row.stops += 1;
      else tally.set(st.subjectId, { id: st.subjectId, label: st.subjectLabel || st.subjectId, stops: 1 });
    }
    return [...tally.values()].sort((a, b) => b.stops - a.stops || a.label.localeCompare(b.label));
  }, [stops]);
  const [followId, setFollowId] = useState<string | null>(null);
  /** Whose route is drawn when there are several — the pick, else the busiest. */
  const followed = oneSubject ? null : (subjects.find((x) => x.id === followId) || subjects[0] || null);
  const followedId = followed?.id ?? null;
  const drawStops = useMemo(
    () => (oneSubject ? stops : followedId ? stops.filter((st) => st.subjectId === followedId) : []),
    [oneSubject, stops, followedId],
  );
  /** Cameras ANY result stopped at — highlighted among the site's cameras on either canvas. */
  const visited = useMemo(() => new Set(stops.map((st) => st.cameraId)), [stops]);
  /** What to call the thing being followed in that caption. */
  const subjectWord = stops.find((st) => st.subjectKind)?.subjectKind === 'vehicle'
    ? 'vehicle' : 'person';

  /**
   * ── LOOKUPS, AND WHAT IS DELIBERATELY NOT FETCHED ──────────────────────
   *
   * Only once the panel is open, and only three cheap reads: where every placed camera sits,
   * the layout NAMES, and the geographic placements plus basemap configuration.
   *
   * NO PLAN IMAGE IS FETCHED HERE. A site can hold a dozen floor plans and each is a picture;
   * loading them to find out which ones matter would spend megabytes to answer a question the
   * placements already answer for free. The side nav is built from `layoutsOnRoute` — the plans
   * this trail actually has stops on — and exactly one image is then fetched: the plan being
   * drawn. Switching plans fetches that one, and the adapter caches it per layout+updated_at, so
   * going back and forth costs nothing.
   */
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    (async () => {
      try {
        const [placed, list, geoPlacements, cfg] = await Promise.all([
          mapLayouts.placedCameras().catch(() => ({} as Record<string, MapPlacedCamera>)),
          mapLayouts.list().catch(() => [] as MapLayoutSummary[]),
          geoMap.placements().catch(() => [] as GeoPlacement[]),
          geoMap.config().catch(() => null),
        ]);
        if (!live) return;
        setPlacedCameras(placed);
        setLayouts(list);
        setGeo(geoPlacements);
        setGeoConfig(cfg);
      } catch (err: any) {
        // One failed lookup must not take the panel down — each canvas reports itself as
        // having nothing to draw, which is what an operator can act on.
        if (live) setError(err?.message || 'Could not load camera placements');
      }
    })();
    return () => { live = false; };
  }, [open, mapLayouts, geoMap, nodeKey]);

  /**
   * WHICH PLANS THIS ROUTE TOUCHES, and how much of it each one holds.
   *
   * A camera belongs to exactly one layout, so a route through a building crosses floors and no
   * single plan can hold all of it. Every plan carrying at least one stop is therefore a real
   * answer to "where did they go", and the panel offers all of them rather than picking one and
   * hiding the rest — which is what it did, and which made a two-floor route look like a
   * one-floor route with stops missing.
   *
   * Ordered by coverage so the fullest answer is first in the list.
   */
  const layoutsOnRoute = useMemo(() => {
    if (!placedCameras) return [] as Array<{ id: string; name: string; stops: number }>;
    const tally = new Map<string, { id: string; name: string; stops: number }>();
    // Counted over the STOPS, not the distinct cameras: a plan holding one camera the subject
    // returned to three times holds three of the route's stops, and that is what the numbers
    // beside the names are comparing.
    for (const st of drawStops) {
      const p = placedCameras[st.cameraId];
      if (!p) continue;
      const row = tally.get(p.layout_id);
      if (row) row.stops += 1;
      // The name off the placement itself, so the picker is still labelled when `list()` failed
      // — the two calls are independent and one of them going down must not leave a dropdown of
      // blank rows.
      else tally.set(p.layout_id, { id: p.layout_id, name: p.layout_name || 'Floor plan', stops: 1 });
    }
    return [...tally.values()]
      .sort((a, b) => b.stops - a.stops || a.name.localeCompare(b.name));
  }, [placedCameras, drawStops]);

  /** The operator's pick, once they have made one. Null until then. */
  const [chosenLayout, setChosenLayout] = useState<string | null>(null);

  /**
   * The plan the panel opens on: THE DEPLOYMENT'S DEFAULT, when the route touches it.
   *
   * Most-covered was the old rule and it was the wrong default. The default layout is the one
   * the site's own people chose as the place to look — the ground floor, the yard — so opening
   * anywhere else means the first thing an operator sees is a plan they have to re-orient
   * themselves on. Coverage only breaks the tie when the route never reaches the default.
   */
  const bestLayoutId = useMemo(() => {
    // No route on any plan: still show a plan — the operator's pick, else the deployment's
    // default, else the first — so the centre pane is a floor plan and not a sentence.
    if (!layoutsOnRoute.length) {
      if (chosenLayout && layouts.some((l) => l.id === chosenLayout)) return chosenLayout;
      return (layouts.find((l) => l.is_default) || layouts[0])?.id || null;
    }
    if (chosenLayout && layoutsOnRoute.some((l) => l.id === chosenLayout)) return chosenLayout;
    /**
     * The default layout, but only where it can show a ROUTE — two stops or more.
     *
     * Preferring it outright was a regression. The default is the plan the site opens on, which
     * on a multi-floor estate is the ground floor, and a route that merely passes through
     * reception has ONE stop there. The panel then opened on a plan with a single pin: no line
     * to draw, and the frame zoomed into a patch of wall around it. Both of those read as the
     * canvas being broken, and neither was — it was being shown the wrong plan.
     *
     * So coverage wins wherever the default cannot draw a connection. `layoutsOnRoute` is
     * already sorted by coverage, so its head is the fullest answer available.
     */
    const preferred = layouts.find((l) => l.is_default
      && (layoutsOnRoute.find((r) => r.id === l.id)?.stops || 0) >= 2);
    return preferred?.id || layoutsOnRoute[0].id;
  }, [layoutsOnRoute, layouts, chosenLayout]);

  useEffect(() => {
    if (!open || !bestLayoutId) {
      setDetail(null); setPlanUrl(null); setLayoutSettled(true);
      return undefined;
    }
    let live = true;
    // Re-armed on every change of layout: the trail moved, and what is on screen describes the
    // previous one until this resolves.
    setLayoutSettled(false);
    /**
     * DROP THE OLD PLAN BEFORE FETCHING THE NEW ONE.
     *
     * `detail` lands one await before the image does, and the image is drawn with
     * `preserveAspectRatio="none"` into `image_width × image_height` — the NEW plan's box. Left
     * up, the previous floor's image spent that gap stretched to the next floor's dimensions
     * with the next floor's pins on top of it: the wrong building, the wrong shape, and pins
     * sitting on nothing. Cheaper to show the pins on an empty canvas for one request than to
     * show a picture that is actively lying about where the subject went.
     */
    setPlanUrl(null);
    (async () => {
      try {
        const d = await mapLayouts.get(bestLayoutId);
        if (!live) return;
        setDetail(d);
        const url = await mapLayouts.imageUrl(d);
        if (live) setPlanUrl(url);
      } catch {
        // The pins still draw on a blank plan — knowing the shape of the route is most of it —
        // so only the IMAGE is given up here. A failed `get` leaves no placements either, and
        // `layoutSettled` is what stops that reading as "still loading".
        if (live) setPlanUrl(null);
      } finally {
        if (live) setLayoutSettled(true);
      }
    })();
    return () => { live = false; };
  }, [open, bestLayoutId, mapLayouts]);

  // ── The two paths ───────────────────────────────────────────────────────
  const gisPath = useMemo<Path | null>(() => {
    if (!detail) return null;
    const by = new Map(detail.placements.map((p) => [p.camera_id, p]));
    return buildPath(drawStops, (id) => {
      const p = by.get(id);
      // FRACTIONS, not plan pixels: the canvas multiplies them onto the rectangle the plan was
      // actually drawn in, which is the only box that knows how big the dock made it.
      return p ? { x: p.x, y: p.y } : null;
    });
  }, [drawStops, detail]);

  const geoPath = useMemo<Path | null>(() => {
    if (!geo) return null;
    const by = new Map(geo.map((p) => [p.camera_id, p]));
    return buildPath(drawStops, (id) => {
      const p = by.get(id);
      return p ? { x: p.lng, y: p.lat } : null;
    });
  }, [drawStops, geo]);

  /**
   * Which canvas opens: the Map, always — Track opens on the geographic view and the operator
   * flips to GIS for the floor plan. (It used to open on whichever canvas held more of the
   * trail, which put most sites on GIS.)
   *
   * Only until the operator touches the toggle: `view` non-null is their choice, and a later
   * trail must not overrule it. Someone who switched to Map to see two sites is still asking
   * about two sites on the next subject.
   */
  /**
   * A canvas is offerable only when it has BOTH the trail's points and something to draw them
   * on. `geoMap.config()` is newer than the services around it, so a node that predates it
   * 404s and leaves the Map with coordinates and no basemap configuration — offered, clicked,
   * and permanently "Loading the map…". The plan is the same bargain in reverse: the image may
   * fail while the placements load, and there the pins ARE still worth drawing, because the
   * shape of the route survives a missing backdrop.
   */
  /**
   * Offerable = has a BACKDROP. A canvas with none of this trail's pins is still a plan or a map
   * worth looking at (and it is where the route will appear), so it is no longer disabled for
   * that; only a node with no plan, or no Map View configuration, greys a button out.
   */
  const gisReady = !!detail && detail.image_width > 0 && detail.image_height > 0;
  const mapReady = !!geoConfig;

  const auto: PathView = 'map';
  /** The operator's choice, else the Map, else whichever is actually drawable. */
  const preferred: PathView = view ?? auto;
  const active: PathView = (preferred === 'gis' && !gisReady && mapReady) ? 'map'
    : (preferred === 'map' && !mapReady && gisReady) ? 'gis'
      : preferred;
  const path = active === 'gis' ? gisPath : geoPath;

  const layoutName = layoutsOnRoute.find((l) => l.id === bestLayoutId)?.name || null;
  /**
   * EVERY lookup has answered — the empty state may not state a verdict before this.
   *
   * The plan is a second fetch, fired only once the first has named which layout to ask for, so
   * "the placements are in" is not the same as "the canvas is in". Leaving it out of this gate
   * meant that for the length of that request the panel declared none of the trail's stops
   * placed, while the tally underneath said how many were.
   */
  const loaded = placedCameras !== null && geo !== null
    && (bestLayoutId === null || layoutSettled);
  /** Has ANYBODY placed a camera on this node, on either canvas? See the empty state. */
  const anyPlacements = (placedCameras ? Object.keys(placedCameras).length : 0) > 0
    || (geo?.length || 0) > 0;

  /**
   * Frame the map on THIS search: the route plus every camera a result stopped at, and nothing
   * else — the site default view shows every camera on the deployment, which is not the
   * question. The map has no viewBox to do it for us.
   *
   * `geoMapReady` is in the deps because MapLibre loads asynchronously: on the click that
   * opens Track the effect ran before the map existed, `fitTo` was a no-op, and the pane sat
   * on the whole-site default. The first framing of each map jumps (`instant`); later ones,
   * after the results change, glide.
   */
  const fitPoints = useMemo(() => {
    const pts = (geo || [])
      .filter((p) => visited.has(p.camera_id))
      .map((p) => ({ lat: p.lat, lng: p.lng }));
    geoPath?.line.forEach((p) => pts.push({ lat: p.y, lng: p.x }));
    return pts;
  }, [geo, visited, geoPath]);
  const [geoMapReady, setGeoMapReady] = useState(false);
  const framedOnce = useRef(false);
  useEffect(() => {
    if (active !== 'map' || !geoMapReady || !fitPoints.length) return;
    mapRef.current?.fitTo(fitPoints, { instant: !framedOnce.current });
    framedOnce.current = true;
  }, [active, geoMapReady, fitPoints]);
  // A new map instance (back from GIS) starts unready and unframed.
  useEffect(() => {
    if (active !== 'map') { setGeoMapReady(false); framedOnce.current = false; }
  }, [active]);


  /**
   * The canvas controls, handed to the shell's header: which canvas, which floor plan, and the
   * tally that explains an empty one. They belong up there rather than over the drawing, which
   * is the thing the operator opened the dock to look at.
   */
  const routable = drawStops.length > 0;
  const canvasControls = (
          <>
            {!oneSubject && subjects.length > 1 && (
              <label data-tour="search-track-following" className="flex items-center gap-1 text-[11px] text-muted-foreground">
                Following
                <select
                  value={followedId ?? ''}
                  onChange={(e) => setFollowId(e.target.value)}
                  title={`${subjects.length} subjects in these results — one route is drawn at a time`}
                  className="max-w-[12rem] rounded-md border border-border bg-card px-1.5 py-0.5 text-[11px] text-foreground"
                >
                  {subjects.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.label} · {x.stops} {x.stops === 1 ? 'stop' : 'stops'}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div data-tour="search-track-canvas" className="flex overflow-hidden rounded-md border border-border" role="radiogroup" aria-label="Path canvas">
              {([
                {
                  key: 'gis' as PathView, label: 'GIS', Icon: MapIcon,
                  placed: gisPath?.placed || 0, ready: gisReady,
                  none: layouts.length || Object.keys(placedCameras || {}).length
                    ? 'That floor plan could not be loaded'
                    : 'No floor plan has been set up on this node (Alert Board ▸ GIS View)',
                },
                {
                  key: 'map' as PathView, label: 'Map', Icon: GlobeAltIcon,
                  placed: geoPath?.placed || 0, ready: mapReady,
                  none: 'This node has no Map View configuration',
                },
              ]).map(({ key, label, Icon, placed, ready, none }) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={active === key}
                  disabled={!ready}
                  onClick={() => setView(key)}
                  title={!ready ? none
                    : routable ? `${placed} of ${drawStops.length} stops on this canvas`
                      : label === 'GIS' ? 'Floor plan' : 'Map'}
                  className={[
                    'flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium transition-colors',
                    'disabled:cursor-not-allowed disabled:opacity-40',
                    active === key ? 'bg-brand text-white' : 'text-foreground hover:bg-muted',
                  ].join(' ')}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/*
              The tally, whenever nothing is drawn.
              
              A panel that can only say "nothing to show" is a panel nobody can debug from the
              outside: the reader cannot tell a trail whose cameras happen to be unplaced from a
              node where placement was never done, and neither can anyone they report it to.
              Two numbers settle it, and they belong in the HEADER so they are readable without
              opening anything.
            */}
            {routable && loaded && !path?.placed && (
              <span className="w-full text-[11px] text-muted-foreground sm:w-auto">
                {Object.keys(placedCameras || {}).length} camera
                {Object.keys(placedCameras || {}).length === 1 ? '' : 's'} pinned on a plan
                {' · '}{geo?.length || 0} with coordinates
                {' · '}this trail visits {cameraIds.length}
              </span>
            )}

            {/*
              NO CONNECTION TO DRAW, said out loud.
              
              One stop on a canvas is a pin, not a route, and a panel called "Tracking path"
              showing a single dot reads as a path that failed to render. It has not: the rest of
              the route is somewhere else, and where is exactly what the picker beside this says.
            */}
            {routable && path && path.placed === 1 && path.total > 1 && (
              <span className="w-full text-[11px] text-amber-500 sm:w-auto">
                Only 1 of {path.total} stops is {active === 'gis' ? 'on this plan' : 'on the map'} —
                no line to draw here
                {active === 'gis' && layoutsOnRoute.length > 1 ? '. Try another plan.' : '.'}
              </span>
            )}

            {/* The coverage statement. Never omitted — see the file header. */}
            {routable && path && path.placed > 1 && (
              <span className="w-full text-[11px] text-muted-foreground sm:w-auto">
                {active === 'gis' && layoutName && <span className="text-foreground">{layoutName} · </span>}
                {path.placed} of {path.total} stops drawn
                {path.placed < path.total && (
                  <span className="text-amber-500">
                    {/* On a route that crosses plans the missing stops are not unplaced, they
                        are ON ANOTHER PLAN — and telling an operator a camera has no pin when
                        it has one upstairs sends them to fix something that is not broken. */}
                    {active === 'gis' && layoutsOnRoute.length > 1
                      ? ` — ${path.total - path.placed} on other plans, so the line skips them`
                      : ` — ${path.total - path.placed} at cameras with no ${
                        active === 'gis' ? 'pin on this plan' : 'coordinates'
                      }, so the line skips them`}
                  </span>
                )}
              </span>
            )}
          </>
  );

  /**
   * Why there is no route on the canvas, laid OVER it as a note. These used to replace the
   * canvas outright, which in a centre pane read as the map failing to render.
   */
  const notice: string | null = !stops.length
    ? 'Search for a person or vehicle — their route is drawn here.'
    : loaded && !path?.placed
      ? (anyPlacements
        ? `None of ${followed ? `${followed.label}’s` : 'this trail’s'} ${drawStops.length} stops are at a camera placed on this ${active === 'gis' ? 'plan' : 'map'}.`
        : 'No camera on this node has been placed yet — pin them under Alert Board ▸ GIS View or Map View.')
      : null;

  /** The site's cameras on the plan being drawn, as backdrop markers. */
  const planCameras = useMemo<BackdropCamera[]>(
    () => (detail?.placements || []).map((p) => ({
      cameraId: p.camera_id, x: p.x, y: p.y, visited: visited.has(p.camera_id),
      rotation: p.rotation, fov: p.fov_deg,
    })),
    [detail, visited],
  );
  /** The same for the map — longitude as x, latitude as y, like the path. */
  const geoCameras = useMemo<BackdropCamera[]>(
    () => (geo || []).map((p) => ({
      cameraId: p.camera_id, x: p.lng, y: p.lat, visited: visited.has(p.camera_id), name: p.camera_name,
      rotation: p.rotation, fov: p.fov_deg,
    })),
    [geo, visited],
  );
  const backdropName = useCallback(
    (c: BackdropCamera) => cameraName(c.cameraId) || c.name || c.cameraId.slice(0, 8),
    [cameraName],
  );

  return (
    <DockShell
      storageKey="trackingPath"
      title="Tracking path"
      sub={stops.length
        ? `${stops.length} ${stops.length === 1 ? 'stop' : 'stops'}${
          identities > 1 ? ` · ${identities} different subjects` : ''}${
          byDescription ? ' · by description' : ''}`
        : 'No route yet'}
      meta={path && path.placed > 0 ? `${path.placed} of ${path.total} drawn` : undefined}
      actions={canvasControls}
      defaultOpen
      fill={fill}
      onCollapse={onCollapse}
    >
      {/*
        `overflow-auto`, so nothing can be clipped unreachably. Both canvases fit themselves to
        this box, so in the ordinary case there is nothing to scroll — but the two empty states
        are paragraphs, and at the minimum height on a narrow window they are taller than the
        panel. Unscrollable, the sentence explaining why there is no route would itself be cut in
        half.
      */}
      {() => (
        <div
          /* `flex-1 min-h-0` inside the shell's flex body: the height comes from the layout in
             both the docked and the full-screen case, which is what the canvas measures itself
             against. A percentage height here would resolve to zero in full screen, and a canvas
             measuring zero draws nothing — the defect this replaced. */
          /* `overflow-auto` is for the TEXT states, which are paragraphs and can outgrow a
             short dock. The canvases never scroll — they are absolutely positioned and clipped,
             precisely so a scrollbar cannot appear under a box whose size they are measuring.
             That loop is what made the plan flicker. */
          className="relative min-h-0 flex-1 overflow-auto border-t border-border"
        >
          {/*
            THE CAPTION A DESCRIPTION-TRAIL MUST CARRY.

            A line between pins means "this moved from here to here". When the stops are held
            together by an appearance rather than an identity, that meaning is a claim the data
            does not make — so the drawing is given the caveat rather than withheld. Overlaid
            rather than stacked above the canvas: it is a note about the drawing, and taking a
            row of height from a dock the operator sized would be charging them for it.
          */}
          {byDescription && (path?.placed || 0) > 0 && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-card px-4 py-1 text-[11px] text-muted-foreground">
              Trail of {tracks} separate tracks matching this description — whether they are one
              {' '}{subjectWord} is not something the cameras can confirm.
            </div>
          )}
          {followed && (path?.placed || 0) > 0 && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 bg-card px-4 py-1 text-[11px] text-muted-foreground">
              Route of <span className="font-medium text-foreground">{followed.label}</span> — one
              of {subjects.length} subjects in these results; pick another under Following.
            </div>
          )}
          {notice && loaded && (active === 'gis' ? gisReady : mapReady) && (
            <div className="pointer-events-none absolute inset-x-0 top-2 z-20 flex justify-center px-4">
              <p className="max-w-lg rounded-md border border-border bg-card px-3 py-1.5 text-center text-[11px] text-muted-foreground shadow-sm">
                {notice}
              </p>
            </div>
          )}
          {!loaded ? (
            /* BEFORE any verdict: "nothing is placed" said while still looking would be a
               confident wrong answer shown on every single open. */
            <div className="flex h-full items-center justify-center text-[12px] text-muted-foreground">
              {bestLayoutId && !layoutSettled
                ? `Loading ${layoutName || 'the floor plan'}…`
                : 'Finding where these cameras are…'}
            </div>
          ) : active === 'gis' && detail ? (
            // `image_width`/`image_height` are the space the placements are fractions OF, so a
            // layout stored without them has no coordinate system and every pin would land on
            // 0,0. Said plainly rather than drawn as an empty grey box.
            detail.image_width > 0 && detail.image_height > 0 ? (
              <div className="flex h-full">
                {/*
                  ── THE PLANS THIS ROUTE ACTUALLY TOUCHES, DOWN THE SIDE ──

                  A camera belongs to exactly one layout, so a route through a building is split
                  across floors and no single plan holds all of it. This used to be a `select` in
                  the dock's title bar: three clicks to compare two floors, and — being a closed
                  control — it never showed that the other floors existed at all until you opened
                  it. On a panel whose whole subject is WHERE, "the rest of this route is
                  upstairs" is not a detail to hide behind a dropdown.

                  ONLY THE PLANS WITH STOPS ON THEM are listed. A site may hold a dozen layouts;
                  the ones this trail never reached are not choices, they are noise, and offering
                  them would invite an operator to open a plan that is empty by construction and
                  read that as the route having been lost. The count beside each name is what
                  makes the split legible without opening anything.

                  Only the CHOSEN plan's image is ever fetched — the list is names and counts,
                  which the placements already carried.
                */}
                {layoutsOnRoute.length > 1 && (
                  <nav
                    aria-label="Floor plans on this route"
                    /* Narrow on a phone, where every pixel it takes is one the plan loses; the
                       names truncate and the counts stay, which is the part that cannot be
                       guessed from the drawing. */
                    className="flex w-28 shrink-0 flex-col overflow-y-auto border-r border-border bg-card sm:w-40"
                  >
                    <p className="sticky top-0 bg-card px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {layoutsOnRoute.length} plans on this route
                    </p>
                    <ul className="pb-1">
                      {layoutsOnRoute.map((l) => {
                        const current = l.id === bestLayoutId;
                        return (
                          <li key={l.id}>
                            <button
                              type="button"
                              onClick={() => setChosenLayout(l.id)}
                              aria-current={current ? 'true' : undefined}
                              title={`${l.stops} of this trail's ${stops.length} stops are on ${l.name}`}
                              className={`flex w-full items-baseline gap-2 border-l-2 px-2.5 py-1.5 text-left transition-colors ${
                                current
                                  ? 'border-brand bg-brand-light'
                                  : 'border-transparent hover:bg-muted'
                              }`}
                            >
                              <span
                                className={`min-w-0 flex-1 truncate text-[11px] ${
                                  current ? 'font-semibold text-foreground' : 'text-foreground'
                                }`}
                              >
                                {l.name}
                              </span>
                              <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                                {l.stops}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </nav>
                )}

                {/* The positioning context the canvas measures itself against — see PlanPath. */}
                <div className="relative min-w-0 flex-1">
                  <PlanPath
                    cameras={planCameras}
                    cameraLabel={backdropName}
                    status={cameraStatus}
                    hasResults={stops.length > 0}
                    path={gisPath!}
                    planUrl={planUrl}
                    width={detail.image_width}
                    height={detail.image_height}
                  />
                </div>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center px-6 text-center text-[12px] text-muted-foreground">
                {layoutName || 'This floor plan'} has no stored image size, so its pins have no
                coordinates to sit on. Re-upload the plan under Alert Board ▸ GIS View.
              </div>
            )
          ) : active === 'map' && geoConfig ? (
            <>
              <GeoTileMap
                handleRef={mapRef}
                onReady={() => setGeoMapReady(true)}
                source={basemap.source}
                initialView={{
                  lat: geoConfig.center_lat,
                  lng: geoConfig.center_lng,
                  zoom: geoConfig.zoom,
                }}
                className="absolute inset-0"
                pins={(project) => geoPath && (
                  <GeoPath
                    path={geoPath}
                    cameras={geoCameras}
                    cameraLabel={backdropName}
                    status={cameraStatus}
                    hasResults={stops.length > 0}
                    project={project}
                  />
                )}
              />
              {/* Above the map's credits button, clear of the description-trail caption. */}
              <BasemapSwitcher
                value={basemap.id}
                onChange={basemap.setId}
                className="absolute bottom-11 right-2 z-20"
              />
            </>
          ) : (
            /* The lookups have SETTLED and there is no backdrop on either canvas. Said as a fact
               rather than left spinning, and saying WHICH of the two set-ups is missing. */
            <div className="flex h-full flex-col items-center justify-center gap-1.5 px-6 text-center">
              <MapPinIcon className="h-5 w-5 text-muted-foreground/50" />
              <p className="text-[12px] font-medium text-foreground">
                {error || 'No floor plan or map is set up on this node yet.'}
              </p>
              <p className="max-w-md text-[11px] leading-relaxed text-muted-foreground">
                Upload a floor plan and pin cameras under Alert Board ▸ GIS View, or configure
                Alert Board ▸ Map View and give cameras coordinates. The route is drawn here once
                one of those exists.
              </p>
              {notice && <p className="max-w-md text-[11px] text-muted-foreground">{notice}</p>}
            </div>
          )}
        </div>
      )}
    </DockShell>
  );
}

// ─── The two canvases ───────────────────────────────────────────────────────

/** Undoes GeoTileMap's in-flight zoom stretch about the element's own origin. */
const UNSCALE = 'scale(calc(1 / var(--map-k, 1)))';

/** Shared ink, so a route means the same thing on either canvas. */
const LINE = 'var(--brand)';
const START = '#10b981';

/**
 * A connection's stop numbers, centred on (0, 0) — the caller places it.
 *
 * A constant screen size on both canvases, like the pins' stop badges, and drawn as an outline
 * (card fill, brand ink) so it cannot be mistaken for one of those solid stop badges.
 */
function EdgeBadge({ text, style }: { text: string; style?: CSSProperties }) {
  const w = text.length * 6 + 10;
  return (
    <g style={style}>
      <rect x={-w / 2} y={-8} width={w} height={16} rx={8} fill="var(--card)" stroke={LINE} strokeWidth={1.5} />
      <text
        x={0}
        y={0}
        dy="0.35em"
        textAnchor="middle"
        fontSize={10}
        fontWeight={700}
        fill={LINE}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {text}
      </text>
    </g>
  );
}

/**
 * The plan canvas — an SVG framed on the path, with the plan behind it.
 *
 * No pan or zoom. The panel is fifteen rems tall and the frame is computed from the path
 * itself, so there is nothing for a reader to navigate to; a pan control here would be a
 * control whose only use is undoing the framing the panel just did for them.
 */
/**
 * THE ALERT BOARD'S PIN, with the trail laid on it.
 *
 * Every placed camera is drawn with `MapMarker` — the same disc, facing cone and Live/REC dots
 * an operator already reads on GIS View and Map View — so a camera looks the same wherever it
 * appears. A camera ON the route is shown selected (brand ring, name visible) and carries its
 * stop number(s) in a badge; cameras no result touched are dimmed, exactly as a board filter
 * dims what it excluded, so the trail's cameras read first without the rest vanishing.
 *
 * `left`/`top` are pixels in whatever layer the caller draws into: the plan's container, or the
 * geographic map's pin layer (which counter-scales via `--map-k`, honoured by both the marker
 * and the badge).
 */
function CameraPins({
  pins, status, hasResults, label, ordinalsOf, total,
}: {
  pins: Array<BackdropCamera & { left: number; top: number }>;
  status: CameraStatus;
  hasResults: boolean;
  label: (c: BackdropCamera) => string;
  ordinalsOf: (cameraId: string) => number[] | undefined;
  total: number;
}) {
  return (
    <>
      {pins.map((c) => {
        const cam = status[c.cameraId];
        // Unknown status (feed failed or camera missing from it) draws as online — a pin that
        // claims a camera is down because a lookup failed would be a false alarm.
        const live = cam ? cam.live : true;
        const recording = cam ? cam.recording : true;
        const ordinals = ordinalsOf(c.cameraId);
        return (
          <MapMarker
            key={c.cameraId}
            name={ordinals ? `${label(c)} — stop ${ordinals.join(', ')}` : label(c)}
            colorState={live || recording ? 'grey' : 'offline'}
            live={live}
            recording={recording}
            left={c.left}
            top={c.top}
            rotation={c.rotation ?? 0}
            fov={c.fov ?? DEFAULT_FOV}
            selected={!!ordinals}
            dimmed={hasResults && !c.visited}
          />
        );
      })}
      {/* Stop numbers, above every pin, so a dense plan never hides one under a neighbour. */}
      {pins.map((c) => {
        const ordinals = ordinalsOf(c.cameraId);
        if (!ordinals) return null;
        const first = ordinals.includes(1);
        const last = ordinals.includes(total);
        return (
          <div
            key={`stop-${c.cameraId}`}
            aria-hidden
            className="pointer-events-none absolute"
            style={{
              left: c.left,
              top: c.top,
              zIndex: 30,
              transformOrigin: '0 0',
              transform: 'scale(calc(1 / var(--map-k, 1)))',
            }}
          >
            <span
              className="absolute flex h-[18px] min-w-[18px] items-center justify-center whitespace-nowrap rounded-full px-1 text-[10px] font-bold leading-none text-white shadow ring-2 ring-white"
              style={{
                left: -24,
                top: -26,
                backgroundColor: first ? START : last ? '#dc2626' : '#2c3e50',
              }}
            >
              {ordinals.join(',')}
            </span>
          </div>
        );
      })}
    </>
  );
}

function PlanPath({
  path, planUrl, width, height, cameras = [], cameraLabel, status = {}, hasResults = false,
}: {
  cameras?: BackdropCamera[];
  cameraLabel?: (c: BackdropCamera) => string;
  status?: CameraStatus;
  hasResults?: boolean;
  path: Path;
  planUrl: string | null;
  width: number;
  height: number;
}) {
  /**
   * THE CANVAS IS SIZED FROM ITS CONTAINER, IN PIXELS.
   *
   * It used to size itself: `h-full w-full` on the `<svg>`, and a ResizeObserver on that same
   * element to read back the shape the crop had to match. That is a circle, and it breaks
   * wherever the percentage height fails to resolve — which is every moment the chain above it
   * has no definite height: the first paint before the dock is measured, the switch into full
   * screen, the frame after a drag. The element then reports a height of zero, and a canvas that
   * measures zero draws nothing — which is what "the overlay is not rendering properly" looks
   * like.
   *
   * So the CONTAINER is measured instead, and the svg is given explicit `width` and `height`
   * attributes from it. Nothing about the drawing depends on CSS resolving a percentage any
   * more: the box is a number, the viewBox is computed from that number, and the two cannot
   * disagree.
   */
  const boxRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;

    const read = () => {
      // `clientWidth/Height`, not the bounding rect: the rect includes any border and is affected
      // by a CSS transform, and an svg sized a pixel larger than its box is an svg with a
      // scrollbar next to it.
      const w = el.clientWidth;
      const h = el.clientHeight;
      /**
       * A ZERO IS NOT A SIZE, it is a moment.
       *
       * The box reports 0×0 while the dock is folding, between a tab switch and the layout that
       * follows it, and on the frame a parent is re-created. Taking those literally blanks the
       * canvas — `size.w > 0` guards the draw — and then restores it one frame later, which is
       * a flicker with no cause the operator can see. The last real measurement is still the
       * best answer until a new real one arrives.
       */
      if (w === 0 || h === 0) return;
      setSize((prev) => (
        // Sub-pixel churn is not a change worth a render, and re-rendering from inside a
        // ResizeObserver on every tick is how an observer loop starts.
        Math.abs(prev.w - w) < 1 && Math.abs(prev.h - h) < 1 ? prev : { w, h }
      ));
    };
    read();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /**
   * IS THE PLAN BEING DRAWN STRETCHED, and if so, say so.
   *
   * The image is drawn with `preserveAspectRatio="none"` into `image_width × image_height`,
   * because the placements are fractions OF those numbers — a pin only lands on the door it was
   * put on if the plan occupies exactly that box. The GIS editor draws it the same way, so the
   * two agree.
   *
   * But when the stored numbers do not match the file (a re-upload at a different size, an
   * export scaled on the way out), that agreement is a distortion: the plan is squashed or
   * pulled, and at any magnification it looks wrong in a way no amount of framing can fix. The
   * operator cannot see WHY from the picture, so the panel measures the file and tells them —
   * with the one action that fixes it, which is re-uploading the plan against its real size.
   */
  const [naturalAspect, setNaturalAspect] = useState(0);
  useEffect(() => {
    setNaturalAspect(0);
    if (!planUrl || typeof Image === 'undefined') return undefined;
    let live = true;
    const probe = new Image();
    probe.onload = () => {
      if (live && probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        setNaturalAspect(probe.naturalWidth / probe.naturalHeight);
      }
    };
    // A plan that will not load is already reported by the panel's own empty states.
    probe.src = planUrl;
    return () => { live = false; };
  }, [planUrl]);

  // 5%: below that the difference is a rounding artefact of the upload, not something an
  // operator would see or should be nagged about.
  const stretched = naturalAspect > 0 && height > 0
    && Math.abs(naturalAspect / (width / height) - 1) > 0.05;
  /**
   * The plan's rectangle inside this container, and the route mapped onto it.
   *
   * EVERYTHING IS IN CONTAINER PIXELS from here down — the svg's viewBox is the container, so a
   * unit is a pixel and nothing is scaled twice. The placements arrive as fractions of the plan,
   * which is what they are stored as, and are multiplied onto the drawn rectangle: the route
   * therefore lands wherever the plan landed, at whatever size the dock gave it.
   */
  const rect = fitPlan(size, { w: width, h: height });

  const nodes = useMemo(
    () => (rect ? path.nodes.map((n) => ({ ...n, ...placeOn(rect, n) })) : []),
    [rect?.x, rect?.y, rect?.w, rect?.h, path.nodes],
  );

  /** The legs, re-pointed at the placed pins. `edgeGeometry` reads only coordinates. */
  const edges = useMemo(() => {
    if (!rect) return [];
    const byId = new Map(nodes.map((n) => [n.cameraId, n]));
    return path.edges
      .map((e) => {
        const from = byId.get(e.from.cameraId);
        const to = byId.get(e.to.cameraId);
        return from && to ? { ...e, from, to } : null;
      })
      .filter(Boolean) as typeof path.edges;
  }, [nodes, path.edges, rect]);

  /**
   * The pin's size, from the DRAWN plan rather than from the stored one.
   *
   * Clamped at both ends: on a plan drawn small a proportional pin is a dot with unreadable
   * digits in it, and on a large one it becomes a saucer covering the room it marks.
   */
  const r = rect ? Math.max(9, Math.min(26, Math.min(rect.w, rect.h) / 26)) : 12;

  /** Every placed camera, in container pixels, for the Alert Board's pins. */
  const pins = useMemo(
    () => (rect ? cameras.map((c) => { const at = placeOn(rect, c); return { ...c, left: at.x, top: at.y }; }) : []),
    [cameras, rect?.x, rect?.y, rect?.w, rect?.h],
  );
  const ordinalsByCamera = useMemo(
    () => new Map(path.nodes.map((n) => [n.cameraId, n.ordinals])),
    [path.nodes],
  );

  return (
    /*
      ── ABSOLUTE, SO THE CANVAS CANNOT MEASURE ITSELF INTO A LOOP ──

      The svg is given explicit pixel dimensions read off this box. In normal flow inside a
      scrollable parent that is a feedback loop waiting to happen: an svg a pixel wider than the
      content box raises a scrollbar, the scrollbar narrows the content box, the next measurement
      shrinks the svg, the scrollbar goes, the box widens — and the plan flickers at the refresh
      rate of the observer.

      Taking the canvas out of flow ends it. An absolutely positioned box is exactly the padding
      box of its container, contributes nothing to the scroll extent, and therefore cannot change
      the thing it is measuring. `overflow-hidden` is the belt to that braces.
    */
    <div ref={boxRef} className="absolute inset-0 overflow-hidden bg-muted">
      {stretched && (
        <p className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-card px-3 py-1 text-[10px] leading-tight text-muted-foreground">
          This plan is stored as {width}×{height}, but the image file is
          {' '}{naturalAspect > width / height ? 'wider' : 'taller'} than that — so it is drawn
          stretched to keep the pins on the features they were placed on. Re-upload it under
          Alert Board ▸ GIS View to fix the shape.
        </p>
      )}
      {/* Drawn only once there IS a box to draw into. A pass at zero would emit a viewBox of
          NaNs, and the browser's answer to that is an empty element that looks exactly like a
          plan which failed to load. */}
      {size.w > 0 && size.h > 0 && rect && (
    <svg
      width={size.w}
      height={size.h}
      viewBox={`0 0 ${size.w} ${size.h}`}
      /* `bg-muted`, not `bg-muted/30`: these palette tokens are bare `var()` values and
         Tailwind v3 drops the opacity modifier on those silently — the canvas had no
         background at all, so a plan that failed to load left the dock looking empty rather
         than blank-but-present. */
      className="block bg-muted"
      role="img"
      aria-label={`The route across ${path.nodes.length} cameras on the floor plan`}
    >
      {/*
        `preserveAspectRatio="none"`, which is SVG for the editor's `object-fill`.
        
        The placements are FRACTIONS of `image_width × image_height`, so the plan has to occupy
        exactly that box for a pin to land on the door it was put on. The default —
        `xMidYMid meet` — fits the image INSIDE the box instead, and wherever the stored
        dimensions disagree with the file's own aspect (a re-upload, a resized export) that
        inset the drawing while leaving the pins where they were: a plan that looks the wrong
        size with its markers floating off the features.
        
        Matching the editor is not a stylistic choice here. GIS View draws it stretched, so a
        route drawn any other way would put the same camera in two different places in two
        parts of the product.
      */}
      {planUrl && (
        <image
          href={planUrl}
          x={rect.x}
          y={rect.y}
          width={rect.w}
          height={rect.h}
          preserveAspectRatio="none"
        />
      )}

      {/* One line per camera PAIR, carrying every move along it — see `PathEdge`. */}
      {edges.map((e) => {
        const g = edgeGeometry(e);
        return (
          <g key={`${e.from.cameraId}-${e.to.cameraId}`}>
            <title>{edgeTitle(e)}</title>
            {/* A pale casing under the ink. A floor plan is line art in the same greys as the
                route, and without it a leg crossing a wall disappears into it. */}
            <path d={g.d} fill="none" stroke="var(--card)" strokeWidth={r * 0.46} strokeLinecap="round" opacity={0.85} />
            <path d={g.d} fill="none" stroke={LINE} strokeWidth={r * 0.22} strokeLinecap="round" />
            {/* The arrowheads carry the DIRECTION, which is the whole difference between a
                route and a set of lines — one per way the connection was walked. */}
            {g.arrows.map((a, i) => (
              <path
                key={i}
                d={`M ${-r * 0.34} ${-r * 0.26} L ${r * 0.3} 0 L ${-r * 0.34} ${r * 0.26} Z`}
                transform={`translate(${a.x} ${a.y}) rotate(${a.angle})`}
                fill={LINE}
              />
            ))}
            <EdgeBadge text={edgeBadge(e)} style={{ transform: `translate(${g.mx}px, ${g.my}px)` }} />
          </g>
        );
      })}

      <text x={size.w / 2} y={size.h} className="sr-only">
        {`${path.placed} of ${path.total} stops`}
      </text>
    </svg>
      )}
      {size.w > 0 && size.h > 0 && rect && (
        <CameraPins
          pins={pins}
          status={status}
          hasResults={hasResults}
          label={(c) => (cameraLabel ? cameraLabel(c) : c.cameraId.slice(0, 8))}
          ordinalsOf={(id) => ordinalsByCamera.get(id)}
          total={path.total}
        />
      )}
    </div>
  );
}

/**
 * The geographic canvas — the same path, projected into the map's layer space.
 *
 * Marks are a CONSTANT SCREEN SIZE here, unlike the plan's. Between settles GeoTileMap
 * stretches the whole layer to follow an animating zoom (`--map-k`), so the geometry rides
 * along exactly while strokes stay `non-scaling-stroke` and every stop badge and arrowhead
 * counter-scales about its own point — the route never thickens or balloons mid-zoom.
 */
function GeoPath({
  path, project, cameras = [], cameraLabel, status = {}, hasResults = false,
}: {
  cameras?: BackdropCamera[];
  cameraLabel?: (c: BackdropCamera) => string;
  status?: CameraStatus;
  hasResults?: boolean;
  path: Path;
  project: (lat: number, lng: number) => { x: number; y: number };
}) {
  /**
   * The edges re-laid in SCREEN space.
   *
   * The bow is a fraction of the leg's length, so it has to be computed after projection or a
   * zoom would change the curvature — two cameras a street apart would bow like a hairpin at
   * one zoom and like a straight line at another.
   */
  const screen = path.edges.map((e) => ({
    key: `${e.from.cameraId}-${e.to.cameraId}`,
    title: edgeTitle(e),
    badge: edgeBadge(e),
    ...edgeGeometry({
      ...e,
      from: { ...e.from, ...project(e.from.y, e.from.x) },
      to: { ...e.to, ...project(e.to.y, e.to.x) },
    }),
  }));

  const ordinalsByCamera = new Map(path.nodes.map((n) => [n.cameraId, n.ordinals]));
  const pins = cameras.map((c) => { const at = project(c.y, c.x); return { ...c, left: at.x, top: at.y }; });

  return (
    <>
    {/* The legs under the pins: an svg layer of lines and arrowheads only. */}
    <svg
      className="pointer-events-none absolute left-0 top-0 overflow-visible"
      width={1}
      height={1}
      role="img"
      aria-label={`The route across ${path.nodes.length} cameras on the map`}
    >
      {screen.map((g) => (
        <g key={g.key}>
          <title>{g.title}</title>
          <path d={g.d} fill="none" stroke="var(--card)" strokeWidth={6} strokeLinecap="round" opacity={0.85} vectorEffect="non-scaling-stroke" />
          <path d={g.d} fill="none" stroke={LINE} strokeWidth={3} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
          {g.arrows.map((a, i) => (
            <path
              key={i}
              d="M -5 -4 L 5 0 L -5 4 Z"
              style={{ transform: `translate(${a.x}px, ${a.y}px) rotate(${a.angle}deg) ${UNSCALE}` }}
              fill={LINE}
            />
          ))}
          <EdgeBadge text={g.badge} style={{ transform: `translate(${g.mx}px, ${g.my}px) ${UNSCALE}` }} />
        </g>
      ))}
    </svg>
    <CameraPins
      pins={pins}
      status={status}
      hasResults={hasResults}
      label={(cam) => (cameraLabel ? cameraLabel(cam) : cam.cameraId.slice(0, 8))}
      ordinalsOf={(id) => ordinalsByCamera.get(id)}
      total={path.total}
    />
    </>
  );
}
